/**
 * Centralized Shopify Configuration
 * Single source of truth for all Shopify settings
 */

/**
 * Strip protocol and trailing slashes from domain strings
 */
function stripProtocol(input: string): string {
  return input.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
}

/**
 * Shopify Storefront API domain (without protocol)
 * Used for GraphQL API calls
 */
export const SHOPIFY_STOREFRONT_DOMAIN = stripProtocol(
  import.meta.env.VITE_SHOPIFY_STOREFRONT_DOMAIN || ''
);

/**
 * Shopify Storefront Access Token
 * Public token for client-side API calls
 */
export const SHOPIFY_STOREFRONT_TOKEN =
  import.meta.env.VITE_SHOPIFY_STOREFRONT_TOKEN || '';

/**
 * Shopify API Version
 * Format: YYYY-MM (e.g., 2024-01)
 */
export const SHOPIFY_API_VERSION =
  import.meta.env.VITE_SHOPIFY_API_VERSION || '2024-01';

/**
 * Shopify Shop Domain (without protocol)
 * Used for customer account URLs
 * Falls back to storefront domain if not specified
 */
export const SHOPIFY_SHOP_DOMAIN = stripProtocol(
  import.meta.env.VITE_SHOPIFY_SHOP_DOMAIN || SHOPIFY_STOREFRONT_DOMAIN
);

/**
 * Check if Shopify is properly configured
 */
export const isShopifyConfigured = Boolean(
  SHOPIFY_STOREFRONT_DOMAIN && SHOPIFY_STOREFRONT_TOKEN
);

/**
 * Get the full Shopify Storefront API endpoint URL
 * @returns Full GraphQL endpoint URL
 */
export function getStorefrontEndpoint(): string {
  if (!SHOPIFY_STOREFRONT_DOMAIN) {
    console.warn('Shopify Storefront domain not configured');
    return '';
  }
  return `https://${SHOPIFY_STOREFRONT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
}

/**
 * Get the Shopify customer account base URL
 * @returns Account base URL for login/register/account pages
 */
export function getAccountBaseUrl(): string {
  if (!SHOPIFY_SHOP_DOMAIN) {
    console.warn('Shopify shop domain not configured');
    return '';
  }
  return `https://${SHOPIFY_SHOP_DOMAIN}/account`;
}

/**
 * Get the Shopify account URL with optional path and return URL
 * @param path - Optional path to append (e.g., '/login', '/register')
 * @param returnTo - Optional return URL after action
 * @returns Full account URL
 */
export function getAccountUrl(path: string = '', returnTo?: string): string {
  const baseUrl = getAccountBaseUrl();
  if (!baseUrl) {
    return '';
  }
  
  const url = `${baseUrl}${path}`;
  if (returnTo) {
    return `${url}?return_url=${encodeURIComponent(returnTo)}`;
  }
  return url;
}

/**
 * Log configuration status (for debugging)
 * Call this in development to verify environment variables are loaded
 */
export function logShopifyConfig(): void {
  if (import.meta.env.DEV) {
    console.group('🛍️ Shopify Configuration');
    console.log('Configured:', isShopifyConfigured);
    console.log('Storefront Domain:', SHOPIFY_STOREFRONT_DOMAIN || '❌ Not set');
    console.log('Shop Domain:', SHOPIFY_SHOP_DOMAIN || '❌ Not set');
    console.log('API Version:', SHOPIFY_API_VERSION);
    console.log('Token Present:', SHOPIFY_STOREFRONT_TOKEN ? '✅ Yes' : '❌ No');
    console.log('Storefront Endpoint:', getStorefrontEndpoint() || '❌ Not available');
    console.log('Account Base URL:', getAccountBaseUrl() || '❌ Not available');
    console.groupEnd();
  }
}
